import { LeadScore } from "../../../types"
import LeadScoreCard from "./lead-score-card"
import { cn } from "../../utils/classnames"
import { leadScoreTableRowGridClass } from "./lead-score-table-grid"

interface Props {
  scores: LeadScore[]
  selectedIds: Set<string>
  optimisticAckedIds: Set<string>
  onToggleSelect?: (id: string) => void
  onSelectAll?: () => void
  onClearSelection?: () => void
  onAcknowledge?: (id: string) => void
  onUndoAcknowledge?: (id: string) => void
}

function ListColumnHeaders({ showCheckbox }: { showCheckbox: boolean }) {
  const gridClass = leadScoreTableRowGridClass(showCheckbox)

  return (
    <div
      className={cn(
        gridClass,
        "hidden md:grid",
        "text-sm font-medium uppercase tracking-wide text-neutral-500"
      )}
    >
      {showCheckbox ? <span aria-hidden className="md:min-h-px" /> : null}
      <span className="text-left">Score</span>
      <span className="text-left">Lead</span>
      <span className="text-left">Contact</span>
      <span className="text-left">Assigned</span>
      <span className="text-left">Action</span>
    </div>
  )
}

const LeadScoreList = ({
  scores,
  selectedIds,
  optimisticAckedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onAcknowledge,
  onUndoAcknowledge
}: Props) => {
  const selectionEnabled =
    onToggleSelect != null && onSelectAll != null && onClearSelection != null

  const allSelected =
    selectionEnabled &&
    scores.length > 0 &&
    scores.every(s => selectedIds.has(s.id))
  const someSelected = scores.some(s => selectedIds.has(s.id))

  return (
    <>
      {selectionEnabled ? (
        <div className="flex items-center gap-3 border-b border-neutral-200 px-3 py-2.5 sm:px-4">
          <input
            type="checkbox"
            checked={allSelected}
            ref={el => {
              if (el) el.indeterminate = !!(someSelected && !allSelected)
            }}
            onChange={() =>
              allSelected ? onClearSelection!() : onSelectAll!()
            }
            className="h-4 w-4 cursor-pointer rounded border-neutral-300 text-primary-400 focus:ring-primary-400"
            aria-label="Select all leads"
          />
          <span className="text-xs text-neutral-500">
            {scores.length} lead{scores.length !== 1 ? "s" : ""}
            {someSelected ? (
              <span className="ml-1 font-medium text-primary-500">
                ({selectedIds.size} selected)
              </span>
            ) : null}
          </span>
        </div>
      ) : null}

      <ListColumnHeaders showCheckbox={selectionEnabled} />

      <div className="divide-y divide-neutral-200">
        {scores.map((score, index) => (
          <LeadScoreCard
            key={score.id}
            score={score}
            index={index}
            isSelected={selectedIds.has(score.id)}
            isOptimisticallyAcked={optimisticAckedIds.has(score.id)}
            onToggleSelect={onToggleSelect}
            onAcknowledge={onAcknowledge}
            onUndoAcknowledge={onUndoAcknowledge}
          />
        ))}
      </div>
    </>
  )
}

export default LeadScoreList
